import React, { ReactElement, memo } from 'react';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import { Button, Row, Cell, Grid, Paragraph } from '@dexma/ui-components';
import i18n from 'asset/translation';
import { StyledFormSubmitModal } from 'adapter/in/common/components/FormSubmitModal/style';

export type CreateNameFormData = {
    name: string;
};

export type FormSubmitModalProps = {
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    handleCloseModal: () => void;
    handleOnSubmit: () => void;
    title: string;
    dataIdContext: {
        tryAgain: string;
        save: string;
        cancel: string;
    };
    message: string;
    proceedButtonText?: string;
    children: ReactElement;
    handleOnTryAgain?: () => void;
};

const FormSubmitModal = memo(
    (props: FormSubmitModalProps): ReactElement => {
        const {
            showModal,
            handleCloseModal,
            error,
            success,
            isLoading,
            handleOnSubmit,
            title,
            message,
            proceedButtonText,
            children,
            handleOnTryAgain,
        } = props;

        return (
            <StyledFormSubmitModal
                width={600}
                open={showModal}
                onCancel={handleCloseModal}
                title={title}
                footer={
                    <>
                        <Button
                            variant='primary'
                            type={!success ? 'submit' : 'button'}
                            text={proceedButtonText || i18n.t('dashboards.create.saveButton')}
                            data-testid={isLoading ? 'loading' : 'submit'}
                            isLoading={isLoading}
                            iconBefore={success ? 'check' : ''}
                            dataId={props.dataIdContext.save}
                            onClick={handleOnSubmit}
                        />
                        <Button
                            variant='outline'
                            text={i18n.t('dashboards.create.cancelButton')}
                            type='button'
                            data-testid='cancel'
                            onClick={() => {
                                handleCloseModal();
                            }}
                            dataId={props.dataIdContext.cancel}
                        />
                    </>
                }
            >
                <form data-testid='form-submit'>
                    <Grid fluid className='no-padding-grid'>
                        <Row>
                            {error && (
                                <Cell>
                                    <ServerErrorAlert tryAgain={handleOnTryAgain || handleOnSubmit} dataId={props.dataIdContext.tryAgain} />
                                </Cell>
                            )}
                            <Cell xs={12}>
                                <Paragraph margin='1rem 0 1rem 0'>{message} </Paragraph>
                            </Cell>
                            <Cell xs={12}>{children}</Cell>
                        </Row>
                    </Grid>
                </form>
            </StyledFormSubmitModal>
        );
    }
);

export default FormSubmitModal;
